export enum MerchantAccountStatus {
  Disabled = 'Disabled',
  Test = 'Test',
  Live = 'Live',
  Retired = 'Retired',
}

export enum FormMode {
  Edit = 'edit',
  View = 'view',
  New = 'new',
}

export enum CardBrand {
  Visa = 'Visa',
  Mastercard = 'Mastercard',
  Amex = 'American Express',
  Discover = 'Discover',
}

export enum ProcessorName {
  Paya = 'Paya',
  Elavon = 'Elavon',
  Fiserv = 'Fiserv',
  TSYS = 'TSYS',
  NMI = 'NMI',
  Converge = 'Converge',
  ProPay = 'ProPay',
  Worldpay = 'Worldpay',
}

export type ProcessorDetail = {
  phone: string | undefined;
  email: string | undefined;
  website: string | undefined;
  type: ProcessorType;
};

export enum TransactionType {
  TRANSACTION_TYPE_AUTH = 1,
  TRANSACTION_TYPE_CAPTURE = 2,
  TRANSACTION_TYPE_VOID = 3,
  TRANSACTION_TYPE_REFUND = 4,
  TRANSACTION_TYPE_REVERSAL = 5,
  TRANSACTION_TYPE_RECUR = 6,
  TRANSACTION_TYPE_VALIDATE = 7,
  TRANSACTION_TYPE_AUTHCAPTURE = 8
}

export enum TransactionTypeVerb {
  TRANSACTION_TYPE_AUTH_VERB = 'AUTH',
  TRANSACTION_TYPE_CAPTURE_VERB = 'CAPTURE',
  TRANSACTION_TYPE_VOID_VERB = 'VOID',
  TRANSACTION_TYPE_REFUND_VERB = 'REFUND',
  TRANSACTION_TYPE_REVERSAL_VERB = 'REVERSAL',
  TRANSACTION_TYPE_RECUR_VERB = 'RECURRING',
  TRANSACTION_TYPE_VALIDATE_VERB = 'VERIFY',
  TRANSACTION_TYPE_AUTHCAPTURE_VERB = 'AUTHCAPTURE'
}

export enum ACHProcessorName {
  JackHenry = 'Jack Henry',
  PayaDirect = 'Paya Direct',
  NMIECheck = 'NMI e-check',
  AuthNetECheck = 'Auth.net e-check',
}

export enum IntegrationName {
  Campfire = 'Campfire',
  FridayLunch = 'Friday Lunch',
}

export enum GatewayName {
  Swipesum = 'Swipesum (Mastercard)',
  // NMI = "NMI",
  // Converge = "Converge",
  // Mastercard = "Mastercard",
  // AuthNet = "AuthNet",
}

export enum ProcessorType {
  Card = 'Card',
  ACH = 'ACH',
}

export enum ProcessorStatus {
  Disabled = 'Disabled',
  Test = 'Test',
  Live = 'Live',
  Retired = 'Retired',
}

/** Contact info keyed by processor. Incomplete on purpose — missing processors just won't show details. */
export const ProcessorDetails: Partial<Record<ProcessorName, ProcessorDetail>> = {
  [ProcessorName.Paya]: {
    phone: '1-800-261-0240',
    email: 'Support.Paya@nuvei.com',
    website: "https://support.paya.com/",
    type: ProcessorType.ACH,
  },
  [ProcessorName.Elavon]: {
    phone: '1-800-725-1243',
    email: undefined,
    website: "https://www.elavon.com/support-center.html",
    type: ProcessorType.Card,
  },
  [ProcessorName.Fiserv]: {
    phone: '1-888-263-1938 ',
    email: undefined,
    website: "https://merchants.fiserv.com/",
    type: ProcessorType.Card,
  },
  [ProcessorName.TSYS]: {
    phone: '800-654-9256',
    email: 'customersupport@tsys.com',
    website: undefined,
    type: ProcessorType.Card,
  },
  [ProcessorName.Worldpay]: {
    phone: '1-866-622-2390',
    email: undefined,
    website: "https://worldpay.com/en/merchant-support",
    type: ProcessorType.Card,
  },
  // [ProcessorName.NMI]: {
  //   phone: '123-456-7890',
  //   email: 'nmi@example.com',
  //   website: undefined,
  // },
  // [ProcessorName.Converge]: {
  //   phone: '123-456-7890',
  //   email: 'converge@example.com',
  //   website: undefined,
  // },
  // [ProcessorName.ProPay]: {
  //   phone: '123-456-7890',
  //   email: 'propay@example.com',
  //   website: undefined,
  // },
};


const mergedProviders = new Set([
  ...Object.values(ProcessorName),
  ...Object.values(ACHProcessorName),
  ...Object.values(GatewayName),
]);

const Provider = Object.freeze(
  Object.fromEntries([...mergedProviders].map((name) => [name, name])),
);

export type Provider = keyof typeof Provider;

export enum PricingStructure {
  FlatRate = 'Flat Rate',
  CostPlus = 'Cost Plus',
  Tiered = 'Tiered',
}

export enum ContactType {
  Primary = 'Primary',
  PCI = 'PCI Compliance',
  Principle = 'Principle Owner',
  Accounting = 'Accounting',
  Technical = 'Technical',
}

export enum AssignedUserType {
  Sales = 'Sales',
  Success = 'Success',
  Partner = 'Partner',
}

export function isEmailValid(email: string): boolean {
  return (
    /^[a-zA-Z0-9. _-]+@[a-zA-Z0-9. -]+\.[a-zA-Z]{2,4}$/.exec(email) != undefined
  );
}

export const UserRoleHierarchy = {
  'portal.admin': 3,
  'portal.manager': 2,
  'portal.sales': 1,
  'portal.support': 1,
  'portal.merchant': 0,
};

export const TaxTypes = {
  Unknown: { code: '00', description: 'Unknown' },
  FederalNationalSalesTax: {
    code: '01',
    description: 'Federal/National Sales Tax',
  },
  StateSalesTax: { code: '02', description: 'State Sales Tax' },
  CitySalesTax: { code: '03', description: 'City Sales Tax' },
  LocalSalesTax: { code: '04', description: 'Local Sales Tax' },
  MunicipalSalesTax: { code: '05', description: 'Municipal Sales Tax' },
  OtherTax: { code: '06', description: 'Other Tax' },
  ValueAddedTax: { code: '10', description: 'Value Added Tax (VAT)' },
  GoodsAndServicesTax: {
    code: '11',
    description: 'Goods and Services Tax (GST)',
  },
  ProvincialSalesTax: { code: '12', description: 'Provincial Sales Tax' },
  RoomTax: { code: '20', description: 'Room Tax' },
  OccupancyTax: { code: '21', description: 'Occupancy Tax' },
  EnergyTax: { code: '22', description: 'Energy Tax' },
};

export const UnitsOfMeasureMC = {
  ACR: { code: 'ACR', description: 'Acre (4840 yd2)' },
  ASM: { code: 'ASM', description: 'Alcoholic strength by mass' },
  ASV: { code: 'ASV', description: 'Alcoholic strength by volume' },
  AMP: { code: 'AMP', description: 'Ampere' },
  AMH: { code: 'AMH', description: 'Ampere-hour (3,6 kC)' },
  ARE: { code: 'ARE', description: 'Are (100 m2)' },
  BAR: { code: 'BAR', description: 'Bar' },
  BLL: { code: 'BLL', description: 'Barrel (petroleum) (158,987 dm3)' },
  BFT: { code: 'BFT', description: 'Board foot' },
  BQL: { code: 'BQL', description: 'Becquerel' },
  BIL: { code: 'BIL', description: 'Billion EUR' },
  MLD: { code: 'MLD', description: 'Billion US' },
  BX: { code: 'BX', description: 'ox' },
  BHP: { code: 'BHP', description: 'Brake horse power (245,7 watts)' },
  BTU: { code: 'BTU', description: 'British thermal unit (1,055 kilojoules)' },
  BUA: { code: 'BUA', description: 'Bushel (35,2391 dm3)' },
  BUI: { code: 'BUI', description: 'Bushel (36,36874 dm3)' },
  CDL: { code: 'CDL', description: 'Candela' },
  CCT: { code: 'CCT', description: 'Carrying capacity in metric tons' },
  CS: { code: 'CS', description: 'ase' },
  CNT: { code: 'CNT', description: 'Cental GB (45,359237 kg)' },
  CGM: { code: 'CGM', description: 'Centigram' },
  CLT: { code: 'CLT', description: 'Centilitre' },
  CMT: { code: 'CMT', description: 'Centimetre' },
  DTN: {
    code: 'DTN',
    description: 'Centner, metric (100 kg) (syn.: decitonne)',
  },
  WCD: { code: 'WCD', description: 'Cord (3,63 m3)' },
  COU: { code: 'COU', description: 'Coulomb' },
  CKG: { code: 'CKG', description: 'Coulomb per kilogram' },
  DMQ: { code: 'DMQ', description: 'Cubic decimetre' },
  FTQ: { code: 'FTQ', description: 'Cubic foot' },
  INQ: { code: 'INQ', description: 'Cubic inch' },
  MTQ: { code: 'MTQ', description: 'Cubic metre' },
  MQH: { code: 'MQH', description: 'Cubic metre per hour' },
  MQS: { code: 'MQS', description: 'Cubic metre per second' },
  MMQ: { code: 'MMQ', description: 'Cubic millimetre' },
  YDQ: { code: 'YDQ', description: 'Cubic yard' },
  CUR: { code: 'CUR', description: 'Curie' },
  DAY: { code: 'DAY', description: 'Day' },
  DEC: { code: 'DEC', description: 'Decade (ten years)' },
  DAA: { code: 'DAA', description: 'Decare' },
  DLT: { code: 'DLT', description: 'Decilitre' },
  DMT: { code: 'DMT', description: 'Decimetre' },
  CEL: { code: 'CEL', description: 'Degree Celsius' },
  FAH: { code: 'FAH', description: 'Degree Fahrenheit' },
  KEL: { code: 'KEL', description: 'Degree Kelvin: same as Kelvin' },
  DPT: { code: 'DPT', description: 'Displacement tonnage' },
  DZN: { code: 'DZN', description: 'Dozen' },
  DZP: { code: 'DZP', description: 'Dozen packs' },
  DZR: { code: 'DZR', description: 'Dozen pairs' },
  DPC: { code: 'DPC', description: 'Dozen pieces' },
  DRL: { code: 'DRL', description: 'Dozen rolls' },
  DRM: { code: 'DRM', description: 'Drachm GB (3,887935 g)' },
  DRI: { code: 'DRI', description: 'Dram GB (1,771745 g)' },
  DRA: { code: 'DRA', description: 'Dram US (3,887935 g)' },
  BLD: { code: 'BLD', description: 'Dry barrel (115,627 dm3)' },
  GLD: { code: 'GLD', description: 'Dry gallon (4,404884 dm3)' },
  PTD: { code: 'PTD', description: 'Dry pint (0,55061 dm3)' },
  QTD: { code: 'QTD', description: 'Dry quart (1,101221 dm3)' },
  EA: { code: 'EA', description: 'Each' },
  FAR: { code: 'FAR', description: 'Farad' },
  OZI: { code: 'OZI', description: 'Fluid ounce (28,413 cm3)' },
  OZA: { code: 'OZA', description: 'Fluid ounce (29,5735 cm3)' },
  FOT: { code: 'FOT', description: 'Foot (0,3048 m)' },
  GLI: { code: 'GLI', description: 'Gallon (4,546092 dm3)' },
  GBQ: { code: 'GBQ', description: 'Gigabecquerel' },
  GWH: { code: 'GWH', description: 'Gigawatt-hour (1 million kW/h)' },
  GII: { code: 'GII', description: 'Gill (0,142065 dm3)' },
  GIA: { code: 'GIA', description: 'Gill (11,8294 cm3)' },
  GRN: { code: 'GRN', description: 'Grain GB, US (64,798910 mg)' },
  GRM: { code: 'GRM', description: 'Gram' },
  GFI: { code: 'GFI', description: 'Gram of fissile isotopes' },
  GGR: { code: 'GGR', description: 'Great gross (12 gross)' },
  GRO: { code: 'GRO', description: 'Gross' },
  GRT: { code: 'GRT', description: 'Gross (register) ton' },
  SAN: { code: 'SAN', description: 'Half year (six months)' },
  HAR: { code: 'HAR', description: 'Hectare' },
  HBA: { code: 'HBA', description: 'Hectobar' },
  HGM: { code: 'HGM', description: 'Hectogram' },
  DTH: { code: 'DTH', description: 'Hectokilogram' },
  HLT: { code: 'HLT', description: 'Hectolitre' },
  HPA: { code: 'HPA', description: 'Hectolitre of pure alcohol' },
  HMT: { code: 'HMT', description: 'Hectometre' },
  HTZ: { code: 'HTZ', description: 'Hertz' },
  HUR: { code: 'HUR', description: 'Hour' },
  CEN: { code: 'CEN', description: 'Hundred' },
  BHX: { code: 'BHX', description: 'Hundred boxes' },
  HIU: { code: 'HIU', description: 'Hundred international units' },
  CLF: { code: 'CLF', description: 'Hundred leaves' },
  CNP: { code: 'CNP', description: 'Hundred packs' },
  CWA: { code: 'CWA', description: 'Hundredweight US (45,3592 kg)' },
  INH: { code: 'INH', description: 'Inch (25,4 mm)' },
  ITM: { code: 'ITM', description: 'Item' },
  JOU: { code: 'JOU', description: 'Joule' },
  KBA: { code: 'KBA', description: 'Kilobar' },
  KGM: { code: 'KGM', description: 'Kilogram' },
  KCP: { code: 'KPH', description: 'Kilogram of caustic potash' },
  KSH: { code: 'KSH', description: 'Kilogram of caustic soda' },
  KNS: { code: 'KNS', description: 'Kilogram of named substance' },
  KNI: { code: 'KNI', description: 'Kilogram of nitrogen' },
  KPPA: { code: 'KPP', description: 'Kilogram of phosphonic anhydride' },
  KPP: { code: 'KPP', description: 'Kilogram of phosphorus pentoxide' },
  KPH: { code: 'KPH', description: 'Kilogram of potassium hydroxide' },
  KPO: { code: 'KPO', description: 'Kilogram of potassium oxide' },
  KSD: { code: 'KSD', description: 'Kilogram of substance 90 per cent dry' },
  KUR: { code: 'KUR', description: 'Kilogram of uranium' },
  KMQ: { code: 'KMQ', description: 'Kilogram per cubic meter' },
  KGS: { code: 'KGS', description: 'Kilogram per second' },
  KHZ: { code: 'KHZ', description: 'Kilohertz' },
  KJO: { code: 'KJO', description: 'Kilogoule' },
  KMT: { code: 'KMT', description: 'Kilometre' },
  KMH: { code: 'KMH', description: 'Kilometre per hour' },
  KPA: { code: 'KPA', description: 'Kilopascal' },
  KTN: { code: 'KTN', description: 'Kilotonne' },
  KVR: { code: 'KVR', description: 'Kilovar' },
  KVT: { code: 'KVT', description: 'Kilovolt' },
  KVA: { code: 'KVA', description: 'Kilovolt-ampere' },
  KWT: { code: 'KWT', description: 'Kilowatt' },
  KWH: { code: 'KWH', description: 'Kilowatt-hour' },
  KNT: { code: 'KNT', description: 'Knot (1 nautical mile per hour)' },
  LEF: { code: 'LEF', description: 'Leaf' },
  GLL: { code: 'GLL', description: 'Liquid gallon (3,7854l dm3)' },
  PTL: { code: 'PTL', description: 'Liquid pint (0,473176 dm3)' },
  QTL: { code: 'QTL', description: 'Liquid quart (0,946353 dm3)' },
  LTR: { code: 'LTR', description: 'Liter (1 dm3)' },
  LPA: { code: 'LPA', description: 'Liter of pure alcohol' },
  LTN: { code: 'LTN', description: 'Long ton GB, US (1,0160469 t)' },
  LUM: { code: 'LUM', description: 'Lumen' },
  LUX: { code: 'LUX', description: 'Lux' },
  MHZ: { code: 'MHZ', description: 'Megahertz' },
  MAL: { code: 'MAL', description: 'Megalitre' },
  MAM: { code: 'MAM', description: 'Megametre' },
  MPA: { code: 'MPA', description: 'Megapascal' },
  MVA: { code: 'MVA', description: 'Megavolt-ampere (1000 KVA)' },
  MAW: { code: 'MAW', description: 'Megawatt' },
  MWH: { code: 'MWH', description: 'Megawatt-hour (100 kW/h)' },
  MTR: { code: 'MTR', description: 'Metre' },
  MTS: { code: 'MTS', description: 'Metre per second' },
  MSK: { code: 'MSK', description: 'Metre per second squared' },
  CTM: { code: 'CTM', description: 'Metric carat (200 mg = 2.10-4 kg)' },
  TNE: { code: 'TNE', description: 'Metric ton (1000 kg)' },
  MBR: { code: 'MBR', description: 'Millibar' },
  MCU: { code: 'MCU', description: 'Millicurie' },
  MGM: { code: 'MGM', description: 'Milligram' },
  MIT: { code: 'MIT', description: 'Millilitre' },
  MMT: { code: 'MMT', description: 'Millimetre' },
  MIO: { code: 'MIO', description: 'Million' },
  HMQ: { code: 'HMQ', description: 'Million cubic metres' },
  MIU: { code: 'MIU', description: 'Million international units' },
  MIN: { code: 'MIN', description: 'Minute' },
  MON: { code: 'MON', description: 'Month' },
  NMI: { code: 'NMI', description: 'Nautical mile (1852 m)' },
  NTT: { code: 'NTT', description: 'Net (register) ton' },
  NEW: { code: 'NEW', description: 'Newton' },
  NMB: { code: 'NMB', description: 'Number' },
  NAR: { code: 'NAR', description: 'Number of articles' },
  NBB: { code: 'NBB', description: 'Number of bobbins' },
  NCL: { code: 'NCL', description: 'Number of cells' },
  NIU: { code: 'NIU', description: 'Number of international units' },
  NMP: { code: 'NMP', description: 'Number of packs' },
  NMR: { code: 'NMR', description: 'Number of pairs' },
  NPL: { code: 'NPL', description: 'Number of parcels' },
  NPT: { code: 'NPT', description: 'Number of parts' },
  NRL: { code: 'NRL', description: 'Number of rolls' },
  OHM: { code: 'OHM', description: 'Ohm' },
  ONZ: { code: 'ONZ', description: 'Ounce GB, US (28,349523 g)' },
  APZ: {
    code: 'APZ',
    description: 'Ounce GB, US (31,10348 g) (syn: Troy ounce)',
  },
  PK: { code: 'PK', description: 'Package' },
  PAL: { code: 'PAL', description: 'Pascal' },
  DWT: { code: 'DWT', description: 'Pennyweight GB, US (1,555174 g)' },
  PCE: { code: 'PCE', description: 'Piece' },
  PCB: { code: 'PCB', description: 'Piece' },
  PSC: { code: 'PSC', description: 'Piece' },
  PTI: { code: 'PTI', description: 'Pint (0,568262 dm3)' },
  LBR: { code: 'LBR', description: 'Pound GB, US (0,45359237 kg)' },
  PGL: { code: 'PGL', description: 'Proof gallon' },
  LBS: { code: 'LBS', description: 'Pounds' },
  QTI: { code: 'QTI', description: 'Quart (1,136523 dm3)' },
  QAN: { code: 'QAN', description: 'Quarter (of a year)' },
  QTR: { code: 'QTR', description: 'Quarter, GB (12,700586 kg)' },
  RPM: { code: 'RPM', description: 'Revolution per minute' },
  RPS: { code: 'RPS', description: 'Revolution per second' },
  SCO: { code: 'SCO', description: 'Score' },
  SCR: { code: 'SCR', description: 'Scruple GB, US (1,295982 g)' },
  SEC: { code: 'SEC', description: 'Second' },
  SET: { code: 'SET', description: 'Set' },
  SHT: { code: 'SHT', description: 'Shipping ton' },
  SST: { code: 'SST', description: 'Short standard (7200 matches)' },
  SIE: { code: 'SIE', description: 'Siemens' },
  CMK: { code: 'CMK', description: 'Square centimetre' },
  DMK: { code: 'DMK', description: 'Square decimetre' },
  FTK: { code: 'FTK', description: 'Square foot' },
  INK: { code: 'INK', description: 'Square inch' },
  KMK: { code: 'KMK', description: 'Square kilometre' },
  MIK: { code: 'MIK', description: 'Square mile' },
  MMK: { code: 'MMK', description: 'Square millimetre' },
  YDK: { code: 'YDK', description: 'Square yard' },
  WSD: { code: 'WSD', description: 'Standard' },
  ATM: { code: 'ATM', description: 'Standard atmosphere (101325 Pa)' },
  SMI: { code: 'SMI', description: '(Statute) mile (1609,344 m)' },
  STI: { code: 'STI', description: 'Stone GB (6,350293 kg)' },
  ATT: { code: 'ATT', description: 'Technical atmosphere (98066,5 Pa)' },
  DAD: { code: 'DAD', description: 'Ten days' },
  TPR: { code: 'TPR', description: 'Ten pairs' },
  MIL: { code: 'MIL', description: 'Thousand' },
  TAH: { code: 'TAH', description: 'Thousand ampere-hour' },
  MBF: { code: 'MBF', description: 'Thousand board feet (2,36 m3)' },
  TQD: { code: 'TQD', description: 'Thousand cubic metres per day' },
  MBE: { code: 'MBE', description: 'Thousand standard brick equivalent' },
  TSH: { code: 'TSH', description: 'Ton of steam per hour' },
  TSD: { code: 'TSD', description: 'Tonne of substance 90 per cent dry' },
  TRL: { code: 'TRL', description: 'Trillion EUR' },
  ST: { code: 'ST', description: 'Unknown unit of Measure' },
  VLT: { code: 'VLT', description: 'Volt' },
  WTT: { code: 'WTT', description: 'Watt' },
  WHR: { code: 'WHR', description: 'Watt-hour' },
  WEB: { code: 'WEB', description: 'Weber' },
  WEE: { code: 'WEE', description: 'Week' },
};
